import React from 'react';
import { Controller, useWatch } from 'react-hook-form';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import { Switch } from 'elements/Switch';
import { SegmentedControl } from 'elements/SegmentedControl';
import { DatePicker } from 'elements/DatePicker';
import { TimePicker, roundUpToSlot } from 'elements/TimePicker';
import { useMemoFormContext } from '../context/FormContext';
import DescriptionField from '../fields/DescriptionField';

type Mode = 'moment' | 'period';

// The calendar composer: the event's name, then whether it's a single moment
// or a period and whether it lasts all day, then the date and time rows.
// Days and times come from the app's own pickers, which follow the app's
// language.
const CalendarForm: React.FC = () => {
  const t = useTranslations();
  const { isDescriptionVisible, control } = useMemoFormContext();

  const isMoment = useWatch({ control, name: 'isMoment', defaultValue: false });
  const isAllDay = useWatch({ control, name: 'isAllDay', defaultValue: false });

  // A date row: the label on the left, the day, and the time unless the
  // event lasts all day.
  const renderDateRow = (label: string, name: 'startAt' | 'endAt') => (
    <Controller
      name={name}
      control={control}
      defaultValue={roundUpToSlot(new Date())}
      render={({ field }) => {
        const value = field.value
          ? new Date(field.value)
          : roundUpToSlot(new Date());

        return (
          <div className={styles.row.container}>
            <label htmlFor={`${name}-date`} className={styles.row.label}>
              {label}
            </label>

            <DatePicker
              id={`${name}-date`}
              value={value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              className={styles.row.date}
            />

            {!isAllDay && (
              <TimePicker
                id={`${name}-time`}
                label={t('memoComposer.calendar.time')}
                value={value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                className={styles.row.time}
              />
            )}
          </div>
        );
      }}
    />
  );

  return (
    <div className={styles.container}>
      {/* Name input (backed by CalendarMemo.name) */}
      <Controller
        control={control}
        name="name"
        render={({ field: { onChange, value, onBlur } }) => (
          <input
            id="name"
            autoComplete="off"
            // "name" reads as an identity field to password managers.
            data-1p-ignore
            data-lpignore
            value={value ?? ''}
            onChange={onChange}
            onBlur={onBlur}
            className={styles.title}
            placeholder={t('memoComposer.calendar.eventName')}
          />
        )}
        rules={{
          required: {
            value: true,
            message: t('memoComposer.titleRequired'),
          },
        }}
      />

      {isDescriptionVisible && <DescriptionField />}

      <div className={styles.options}>
        <Controller
          name="isMoment"
          control={control}
          render={({ field }) => (
            <SegmentedControl<Mode>
              aria-label={t('memoComposer.calendar.kind')}
              value={field.value ? 'moment' : 'period'}
              onChange={(mode) => field.onChange(mode === 'moment')}
              options={[
                { value: 'moment', label: t('memoComposer.calendar.moment') },
                { value: 'period', label: t('memoComposer.calendar.period') },
              ]}
            />
          )}
        />

        <Controller
          name="isAllDay"
          control={control}
          render={({ field }) => (
            <Switch
              checked={Boolean(field.value)}
              onChange={field.onChange}
              label={t('memoCard.calendar.allDay')}
            />
          )}
        />
      </div>

      <div className={styles.rows}>
        {isMoment ? (
          renderDateRow(t('memoComposer.calendar.on'), 'startAt')
        ) : (
          <>
            {renderDateRow(t('memoComposer.calendar.starts'), 'startAt')}
            {renderDateRow(t('memoComposer.calendar.ends'), 'endAt')}
          </>
        )}
      </div>
    </div>
  );
};

export default CalendarForm;

const styles = {
  container: 'space-y-4',
  title: classNames(
    'w-full border-none bg-transparent p-0 text-base font-medium outline-hidden',
    'focus:border-transparent focus:outline-hidden focus:ring-0 caret-blue-600',
  ),
  options: 'flex items-center justify-between gap-4',
  rows: 'space-y-2.5',
  row: {
    container: 'flex items-center gap-2.5',
    label: 'w-11 shrink-0 text-sm text-ink-primary',
    date: 'flex-1 text-sm',
    time: 'w-[150px] shrink-0 text-sm',
  },
};
